import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { GetColab } from '../../modules/Colab/types';
import { getCarModelLabel } from './colabModelQuickFilter';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

type ColabStatisticsProps = {
  records: GetColab[];
  totalRecords: number;
};

type CountItem = {
  count: number;
  label: string;
};

type MonthItem = CountItem & {
  key: string;
};

type Statistics = {
  categories: CountItem[];
  countries: CountItem[];
  carModels: CountItem[];
  monthItems: MonthItem[];
  sources: CountItem[];
  uniqueCars: number;
  yearItems: CountItem[];
};

const chartColors = ['#0f766e', '#2563eb', '#7c3aed', '#c2410c', '#4f46e5', '#047857', '#be123c', '#475569'];

const horizontalBarOptions: ChartOptions<'bar'> = {
  indexAxis: 'y',
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
    },
    y: {
      ticks: {
        autoSkip: false,
      },
    },
  },
};

const lineOptions: ChartOptions<'line'> = {
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
    },
  },
};

function normalizeLabel(value: string | undefined | null, fallback = 'Unknown') {
  const normalizedValue = (value || '').trim();
  return normalizedValue || fallback;
}

function getVin(record: GetColab) {
  return normalizeLabel(record.vin || record.VIN, '');
}

function getCountItems(records: GetColab[], getValue: (record: GetColab) => string): CountItem[] {
  const counts = records.reduce((acc, record) => {
    const label = normalizeLabel(getValue(record));
    acc.set(label, (acc.get(label) || 0) + 1);
    return acc;
  }, new Map<string, number>());

  return Array.from(counts.entries())
    .map(([label, count]) => ({ count, label }))
    .sort((first, second) => second.count - first.count || first.label.localeCompare(second.label));
}

function getTimeItems(records: GetColab[]) {
  const monthCounts = new Map<string, number>();
  const yearCounts = new Map<string, number>();

  records.forEach(record => {
    const created = dayjs(record.created);

    if (!created.isValid()) return;

    const monthKey = created.format('YYYY-MM');
    const yearKey = created.format('YYYY');

    monthCounts.set(monthKey, (monthCounts.get(monthKey) || 0) + 1);
    yearCounts.set(yearKey, (yearCounts.get(yearKey) || 0) + 1);
  });

  return {
    monthItems: Array.from(monthCounts.entries())
      .sort(([firstMonth], [secondMonth]) => firstMonth.localeCompare(secondMonth))
      .map(([key, count]) => ({ count, key, label: dayjs(`${key}-01`).format('MMM YYYY') })),
    yearItems: Array.from(yearCounts.entries())
      .sort(([firstYear], [secondYear]) => firstYear.localeCompare(secondYear))
      .map(([label, count]) => ({ count, label })),
  };
}

function getStatistics(records: GetColab[]): Statistics {
  const uniqueCars = new Set(records.map(getVin).filter(Boolean)).size;
  const timeItems = getTimeItems(records);

  return {
    categories: getCountItems(records, record => record.category || 'Uncategorized'),
    countries: getCountItems(records, record => record.vehicleMarket),
    carModels: getCountItems(records, record => getCarModelLabel(record.vehicleModel)),
    monthItems: timeItems.monthItems,
    sources: getCountItems(records, record => record.fromAppName),
    uniqueCars,
    yearItems: timeItems.yearItems,
  };
}

function getBarData(items: CountItem[], label: string): ChartData<'bar'> {
  return {
    labels: items.map(item => item.label),
    datasets: [
      {
        backgroundColor: items.map((_, index) => chartColors[index % chartColors.length]),
        borderRadius: 4,
        data: items.map(item => item.count),
        label,
      },
    ],
  };
}

function getLineData(items: MonthItem[]): ChartData<'line'> {
  return {
    labels: items.map(item => item.label),
    datasets: [
      {
        borderColor: '#0f766e',
        backgroundColor: '#0f766e',
        data: items.map(item => item.count),
        label: 'Items',
        pointRadius: 3,
        tension: 0.35,
      },
    ],
  };
}

function formatCount(value: number) {
  return value.toLocaleString();
}

function getChartHeight(items: CountItem[]) {
  return Math.max(220, Math.min(520, items.length * 34));
}

function SummaryCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <dt className="text-xs font-semibold uppercase text-gray-500">{label}</dt>
      <dd className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">{value}</dd>
    </div>
  );
}

function ChartCard({
  children,
  emptyText,
  items,
  title,
}: {
  children: React.ReactNode;
  emptyText: string;
  items: CountItem[];
  title: string;
}) {
  return (
    <section className="rounded-md border border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <span className="shrink-0 rounded-sm bg-gray-100 px-2 py-1 text-xs font-semibold tabular-nums text-gray-600">
          {items.length}
        </span>
      </div>
      {items.length > 0 ? children : <p className="text-sm text-gray-500">{emptyText}</p>}
    </section>
  );
}

function BarChartSection({ emptyText, items, title }: { emptyText: string; items: CountItem[]; title: string }) {
  return (
    <ChartCard emptyText={emptyText} items={items} title={title}>
      <div style={{ height: getChartHeight(items) }}>
        <Bar data={getBarData(items, title)} options={horizontalBarOptions} />
      </div>
    </ChartCard>
  );
}

function TimeSection({ monthItems, yearItems }: { monthItems: MonthItem[]; yearItems: CountItem[] }) {
  return (
    <section className="rounded-md border border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900">Items over time</h3>
        <span className="shrink-0 rounded-sm bg-gray-100 px-2 py-1 text-xs font-semibold tabular-nums text-gray-600">
          {monthItems.length}
        </span>
      </div>

      {monthItems.length > 0 ? (
        <>
          <div className="h-72">
            <Line data={getLineData(monthItems)} options={lineOptions} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {yearItems.map(item => (
              <div key={item.label} className="rounded-md bg-gray-50 p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">{item.label}</div>
                <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{formatCount(item.count)}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">No dated records available.</p>
      )}
    </section>
  );
}

function ColabStatistics({ records, totalRecords }: ColabStatisticsProps) {
  const stats = useMemo(() => getStatistics(records), [records]);

  return (
    <div>
      <h2 className="mb-2.5 text-xl font-medium">Statistics</h2>

      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard label="Current items" value={formatCount(records.length)} />
        <SummaryCard label="Backend loaded" value={formatCount(totalRecords)} />
        <SummaryCard label="Unique cars" value={formatCount(stats.uniqueCars)} />
        <SummaryCard label="Categories" value={formatCount(stats.categories.length)} />
      </dl>

      <div className="mt-6 space-y-5">
        <BarChartSection emptyText="No car data available." items={stats.carModels} title="Cars" />
        <BarChartSection emptyText="No category data available." items={stats.categories} title="Categories" />
        <BarChartSection emptyText="No country data available." items={stats.countries} title="Countries" />
        <BarChartSection emptyText="No source data available." items={stats.sources} title="Source" />
        <TimeSection monthItems={stats.monthItems} yearItems={stats.yearItems} />
      </div>
    </div>
  );
}

export default ColabStatistics;
